import { useState, useEffect } from "react";
import { useAuth } from '../../hooks/use-auth';
import { useLanguage } from '../../hooks/use-language';
import { usePermissions } from '../../hooks/use-permissions';
import { Link, useLocation } from "wouter";
import { 
  BuildingIcon, 
  LayoutDashboardIcon, 
  CalendarIcon, 
  UsersIcon, 
  FileTextIcon, 
  BanknoteIcon, 
  HammerIcon, 
  BotIcon, 
  SettingsIcon, 
  LogOutIcon,
  MenuIcon,
  X,
  ClipboardCheckIcon,
  BarChart,
  FileText,
  Building,
  HardDrive,
  Clock,
  Palette,
  Settings,
  Wrench,
  Calculator
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import { ThemeToggle } from '../ui/theme-toggle';
import RemodraLogo from '../remodra-logo';
import { Lock } from 'lucide-react';
import { authInterceptor } from '../../lib/auth-interceptor';

export default function MobileSidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const [location] = useLocation();
  const { user, logoutMutation } = useAuth();
  const { t } = useLanguage();

  const handleLogout = () => {
    logoutMutation.mutate();
    setIsOpen(false);
  };

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
  };

  const closeSidebar = () => {
    setIsOpen(false);
  };

  const isActivePath = (path: string) => {
    return location === path;
  };

  const getLinkClass = (path: string) => {
    return `flex items-center px-4 py-3 mx-2 my-1 rounded-xl transition-all duration-300 ease-in-out group ${
      isActivePath(path) 
        ? "bg-gradient-to-r from-green-500/20 to-green-700/20 text-green-500 border border-green-500/30 shadow-lg backdrop-blur-sm" 
        : "text-slate-300 hover:bg-white/10 hover:text-white hover:shadow-md hover:scale-105 hover:translate-x-1"
    }`;
  };

  const getInitials = () => {
    if (!user) return "U";
    return `${user.firstName?.[0] || ""}${user.lastName?.[0] || ""}`;
  };

  // Plan-based access control — aligned with backend PLAN_FEATURES (match desktop sidebar)
  const getPlanAccess = () => {
    let plan = user?.plan;
    if (!plan) {
      try {
        const localUser = localStorage.getItem('user');
        if (localUser) {
          const parsed = JSON.parse(localUser);
          plan = parsed?.plan;
        }
      } catch (e) {
        // ignore
      }
    }
    plan = (plan || 'basic').toString().toLowerCase().trim();

    const planFeatures = {
      basic: ['dashboard', 'clients', 'estimates', 'invoices', 'services', 'settings'],
      pro: ['dashboard', 'clients', 'estimates', 'invoices', 'calendar', 'projects', 'services', 'timeclock', 'settings', 'ai-assistant'],
      business: ['dashboard', 'clients', 'estimates', 'invoices', 'calendar', 'projects', 'agents', 'services', 'timeclock', 'ai-assistant', 'materials', 'settings']
    };

    const planAccess = {
      basic: { features: planFeatures.basic, name: 'Basic Plan', color: 'bg-blue-500' },
      pro: { features: planFeatures.pro, name: 'Pro Plan', color: 'bg-green-500' },
      business: { features: planFeatures.business, name: 'Business Plan', color: 'bg-green-500' }
    };

    return planAccess[plan as keyof typeof planAccess] || planAccess.basic;
  };

  const planAccess = getPlanAccess();
  const { canAccessFeature } = usePermissions();
  const [showAiRoofMeasurement, setShowAiRoofMeasurement] = useState(false);

  // Load contractor settings when route changes or when settings are updated
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await fetch('/api/protected/contractor/settings', {
          credentials: 'include',
          headers: authInterceptor.addAuthHeaders({ 'Content-Type': 'application/json' }),
        });
        if (!res.ok) return;
        const data = await res.json();
        setShowAiRoofMeasurement(Boolean((data as any).showAiRoofMeasurement));
      } catch (error) {
        console.error('Failed to load contractor settings for mobile sidebar:', error);
      }
    };
    loadSettings();

    const handleSettingsUpdated = (event: any) => {
      const nextValue = event.detail?.showAiRoofMeasurement;
      if (typeof nextValue === 'boolean') {
        setShowAiRoofMeasurement(nextValue);
      } else {
        loadSettings();
      }
    };

    window.addEventListener('contractor-settings-updated', handleSettingsUpdated);
    return () => {
      window.removeEventListener('contractor-settings-updated', handleSettingsUpdated);
    };
  }, [location]);

  // Some screens (e.g., full-screen forms) need the toggle hidden so it doesn't cover inputs
  // Some screens disable the toggle themselves, so we no longer hide it here.

  // Some screens (e.g., full-screen forms) need the toggle hidden so it doesn't cover inputs
  // Some screens disable the toggle themselves, so we no longer hide it here.

  // All navigation items (matching desktop sidebar)
  const allNavigationItems = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboardIcon, feature: 'dashboard' },
    { name: 'Calendar', href: '/calendar', icon: CalendarIcon, feature: 'calendar' },
    { name: 'Clients', href: '/clients', icon: UsersIcon, feature: 'clients' },
    { name: 'Estimates', href: '/estimates', icon: FileTextIcon, feature: 'estimates' },
    { name: 'Invoices', href: '/invoices', icon: BanknoteIcon, feature: 'invoices' },
    { name: 'Projects', href: '/projects', icon: HammerIcon, feature: 'projects' },
    { name: 'Services', href: '/simple-pricing', icon: Settings, feature: 'services' },
    { name: 'AI Roof Measurement', href: '/ai-roof-measurement', icon: BotIcon, feature: 'ai-assistant' as const },
    { name: 'Time Clock', href: '/timeclock', icon: Clock, feature: 'timeclock' },
    { name: 'Agents', href: '/agents', icon: Calculator, feature: 'agents' },
    { name: 'Settings', href: '/settings', icon: SettingsIcon, feature: 'settings' },
  ];

  // Filter by plan and permission — AI Roof depends on setting
  const availableItems = allNavigationItems.filter((item) => {
    if (item.feature === 'ai-assistant') {
      if (!showAiRoofMeasurement) return false;
    }
    return planAccess.features.includes(item.feature) && canAccessFeature(item.feature);
  });

  const lockedFeatures = allNavigationItems.filter(
    (item) => !planAccess.features.includes(item.feature)
  );

  // Close sidebar when clicking outside or when dialogs open
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebarElement = document.getElementById("mobile-sidebar");
      const toggleButton = document.getElementById("mobile-sidebar-toggle");
      
      // Check if clicking on a dialog overlay or content
      const target = event.target as HTMLElement;
      const isDialog = target.closest('[role="dialog"]') || 
                       target.closest('.fixed.inset-0.z-50') ||
                       target.closest('[data-radix-dialog-overlay]') ||
                       target.closest('[data-radix-alert-dialog-overlay]');
      
      if (
        sidebarElement && 
        !sidebarElement.contains(target) && 
        toggleButton && 
        !toggleButton.contains(target) &&
        !isDialog
      ) {
        setIsOpen(false);
      }
    };

    // Close sidebar when dialogs open
    const checkForDialogs = () => {
      // Check for open dialogs using multiple selectors
      // Radix UI Dialog overlay has z-50 and bg-black/80
      const hasOpenDialog = 
        document.querySelector('[role="dialog"][data-state="open"]') ||
        document.querySelector('[data-radix-dialog-overlay][data-state="open"]') ||
        document.querySelector('[data-radix-alert-dialog-overlay][data-state="open"]') ||
        document.querySelector('[data-radix-dialog-content][data-state="open"]') ||
        document.querySelector('.fixed.inset-0.z-50[data-state="open"]') ||
        // Check for Dialog overlay (Radix UI uses this)
        document.querySelector('[data-radix-portal] .fixed.inset-0.z-50') ||
        // Check for any element with dialog role in a portal
        document.querySelector('[data-radix-portal] [role="dialog"]') ||
        // Check for DialogContent directly
        document.querySelector('[data-radix-dialog-content]') ||
        // Check for overlay by class pattern
        document.querySelector('body > [class*="fixed"][class*="inset-0"][class*="z-50"]:not([id="mobile-sidebar"])');
      
      if (hasOpenDialog && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      
      // Check for dialogs on DOM mutations (when dialogs are added/opened)
      const observer = new MutationObserver(() => {
        checkForDialogs();
      });
      observer.observe(document.body, { 
        childList: true, 
        subtree: true,
        attributes: true,
        attributeFilter: ['data-state', 'role']
      });
      
      // Also check periodically (fallback)
      const intervalId = setInterval(checkForDialogs, 100);
      
      // Initial check
      checkForDialogs();

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        observer.disconnect();
        clearInterval(intervalId);
      };
    }
  }, [isOpen]);

  // Close sidebar when route changes
  useEffect(() => {
    closeSidebar();
  }, [location]);

  return (
    <>
      {/* Mobile menu toggle button - Must be above TopNav (z-30) and Dialogs (z-55) */}
      <div 
        className="fixed top-4 left-4 md:hidden z-[70]" 
        style={{ 
          zIndex: 70,
          pointerEvents: 'auto',
          position: 'fixed'
        }}
        onClick={(e) => {
          // Stop propagation to prevent dialog from intercepting
          e.stopPropagation();
        }}
        onPointerDown={(e) => {
          // Stop propagation to prevent dialog from intercepting
          e.stopPropagation();
        }}
      >
        <Button
          id="mobile-sidebar-toggle"
          variant="ghost"
          size="icon"
          className="h-12 w-12 bg-gradient-to-br from-green-500 to-green-700 hover:from-green-600 hover:to-green-800 border-2 border-green-600 shadow-2xl rounded-xl transition-all duration-300 hover:scale-110 active:scale-95"
          onClick={(e) => {
            e.stopPropagation();
            toggleSidebar();
          }}
          onPointerDown={(e) => {
            e.stopPropagation();
          }}
          aria-label="Toggle menu"
          style={{ 
            pointerEvents: 'auto',
            backgroundColor: '#16a34a', // green-600 fallback
            color: 'white'
          }}
        >
          <MenuIcon className="h-7 w-7 text-white" style={{ color: 'white', strokeWidth: 2.5 }} />
        </Button>
      </div>

      {/* Sidebar overlay - Below sidebar but above TopNav */}
      <div 
        className={`remodra-mobile-overlay transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={closeSidebar}
        style={{ zIndex: 50 }}
      />

      {/* Mobile sidebar - Highest z-index when open */}
      <aside 
        id="mobile-sidebar"
        className={`remodra-mobile-sidebar flex flex-col ${
          isOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={{ zIndex: 55 }}
      >
        {/* Header */}
        <div className="flex-shrink-0 p-4 flex items-center justify-between border-b border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800">
          <div className="flex items-center">
            <div className="mr-3">
              <RemodraLogo size={32} />
            </div>
            <span className="text-xl font-bold text-green-500">Remodra</span>
          </div>
          <Button
            variant="ghost" 
            size="icon"
            className="text-slate-600 dark:text-slate-400 hover:text-green-500 hover:bg-white/10 dark:hover:bg-white/10 rounded-lg"
            onClick={closeSidebar}
          >
            <X className="h-6 w-6" />
          </Button>
        </div>
        
        {/* Navigation - Scrollable */}
        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto min-h-0">
          {/* Available Features (based on plan) */}
          {availableItems.map((item) => {
            const isActive = location === item.href;
            return (
              <Link
                key={item.name}
                href={item.href}
                className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-all duration-300 ${
                  isActive 
                    ? 'bg-gradient-to-r from-green-500 to-green-700 text-white font-semibold' 
                    : 'text-slate-300 dark:text-slate-300 light:text-slate-700 hover:text-green-500 dark:hover:text-green-500 light:hover:text-green-600 hover:bg-slate-700/50 dark:hover:bg-slate-700/50 light:hover:bg-slate-100'
                }`}
                onClick={closeSidebar}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" />
                <span className="text-sm font-medium">{item.name}</span>
              </Link>
            );
          })}

          {/* Locked features — upgrade to unlock */}
          {lockedFeatures.length > 0 && (
            <>
              <Separator className="my-4" />
              <div className="px-3 py-2">
                <h3 className="text-xs font-semibold text-slate-400 dark:text-slate-400 light:text-slate-600 mb-2">Upgrade to unlock</h3>
                {lockedFeatures.map((item) => (
                  <div
                    key={item.name}
                    className="flex items-center space-x-3 px-3 py-2 text-slate-500 dark:text-slate-500 light:text-slate-600 rounded-lg"
                  >
                    <Lock className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm font-medium">{item.name}</span>
                  </div>
                ))}
                <Link href="/billing" onClick={closeSidebar}>
                  <Button variant="outline" size="sm" className="mt-2 w-full text-green-600 border-green-500/50 hover:bg-green-500/10">
                    Upgrade plan
                  </Button>
                </Link>
              </div>
            </>
          )}
        </nav>

        {/* User Profile - Fixed Footer */}
        <div className="flex-shrink-0 p-4 border-t border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800">
          <div className="flex items-center space-x-3">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-green-500 text-slate-900 text-sm font-semibold">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-200 dark:text-slate-200 light:text-slate-800 truncate">
                {typeof user?.firstName === 'string' ? user.firstName : ''} {typeof user?.lastName === 'string' ? user.lastName : ''}
              </p>
              <p className="text-xs text-slate-400 dark:text-slate-400 light:text-slate-600 truncate">
                @{typeof user?.username === 'string' ? user.username : user?.email || ''}
              </p>
            </div>
          </div>
          
          <div className="mt-3 space-y-2">
            <div className="flex items-center justify-start space-x-2">
              <Badge className={`${planAccess.color} text-white text-xs`}>
                {planAccess.name}
              </Badge>
            </div>
            <div className="flex items-center gap-2">
              <ThemeToggle />
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="flex-1 justify-start text-slate-400 dark:text-slate-400 light:text-slate-600 hover:text-red-400 hover:bg-red-400/10"
              >
                <LogOutIcon className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}
